/**
 * Decision rules cluster for Engine Editor — transformation × decision cell
 * per scope-doc §2 cell-by-cell routing. 5 sub-region cards tiled within
 * cluster cell per Atlas Day 2 visual-layout commitment A.1 + (b).
 *
 * Cluster header inherits Atlas DecisionRulesCluster.tsx verbatim per
 * scope-doc §3.2 copy principle (read-altitude cluster framing shared
 * between Atlas + Engine Editor).
 *
 * Per scope-doc §2 cell routing (lines 221-226):
 *   - MAP enforcement (§6.6.1): `brands.enforces_map` 4a + `products.map_price` 2
 *   - Credit gate (§6.6.2): `credit_limit`/`payment_terms` 4b-deferred + `credit_ledger` 3b
 *   - Path eligibility (§6.6.3): `fpc.is_active`/`manufacturer_distributors` 4b-deferred
 *   - Restock urgency tiers (§6.6.4): `restock_urgency_red/yellow` 1b
 *   - Buy Score composition (§6.6.5): `buy_score_weight_components` 4a (federated to Weight components)
 *
 * Day 1 scaffolding: positive 4a affordances (MAP enforcement enforces_map,
 * Buy Score composition full row) wire at Day 2 + Day 3. All sub-regions
 * render Day-1 negative-affordance content per scope-doc §4.2 sub-shape
 * vocabulary verbatim copy framing.
 *
 * Sub-region components live as private const components inline at Day 1
 * per build-prompt P4.3 (no separate sub-region files yet). Future Day 2/3
 * may extract specific sub-regions to separate files when positive 4a
 * affordances + composition rendering grow beyond inline scope.
 */

import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Route, AlertTriangle, Calculator } from 'lucide-react';
import {
  fetchTransformationRowSubstrate,
  type BuyScoreCompositionSubstrate,
} from '../../../lib/atlas/transformationRowSubstrate';
import {
  URL_FINANCE_CREDIT_AND_CASH,
  URL_DIRECTORY_DISTRIBUTORS,
  URL_SETTINGS_CHANNELS_AND_PATHS,
  URL_ENGINE_EDITOR_WEIGHT_COMPONENTS,
} from '../../../lib/engine-editor/engineEditorUrls';
import EngineEditorMapEnforcementSubRegion from './EngineEditorMapEnforcementSubRegion';

// Natural-language Oxford-comma joiner (mirrors Atlas BuyScoreCompositionCard
// joinList per scope-doc §3.2 copy principle).
function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

// ── Sub-region: MAP enforcement (§6.6.1) ─────────────────────────────────────
// Day 3 P1+P2+P3 extracted to EngineEditorMapEnforcementSubRegion.tsx per Day 1
// P4.3 anticipation (extraction triggered by substantial positive 4a affordance
// + brand selector + gate-refusal-with-pathway orchestration).
// `brands.enforces_map` V1 4a (column-surgical extraction per scope §3.7) +
// gate-refusal-with-pathway gating per scope §6.2.

// ── Sub-region: Credit gate (§6.6.2) ────────────────────────────────────────
// distributors.credit_limit + payment_terms are 4b-deferred; credit_ledger is 3b.
function CreditGateSubRegion(): JSX.Element {
  return (
    <div
      id="credit-gate"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <CreditCard size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Credit gate</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Per-distributor credit terms (`credit_limit` + `payment_terms`) and ledger
            (`credit_ledger`) gating PO submission when available credit insufficient.
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Edit `credit_limit` + `payment_terms` at{' '}
        <a
          href={URL_DIRECTORY_DISTRIBUTORS}
          className="text-accent hover:underline not-italic"
        >
          Directory → Distributors
        </a>
        . Engine Editor absorbs at V2 (build-window). Credit ledger — append-only;
        entries created at PO submission cascade. View at{' '}
        <a
          href={URL_FINANCE_CREDIT_AND_CASH}
          className="text-accent hover:underline not-italic"
        >
          Finance → Credit &amp; Cash
        </a>
        .
      </p>
    </div>
  );
}

// ── Sub-region: Path eligibility (§6.6.3) ───────────────────────────────────
// fpc.is_active + manufacturer_distributors are 4b-deferred (Channels & Paths Engine Tuning).
function PathEligibilitySubRegion(): JSX.Element {
  return (
    <div
      id="path-eligibility"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <Route size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Path eligibility</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Per-path activation (`fulfillment_path_config.is_active`) and per-manufacturer
            distributor eligibility (`manufacturer_distributors`) gating which paths are
            considered for products.
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Edit at{' '}
        <a
          href={URL_SETTINGS_CHANNELS_AND_PATHS}
          className="text-accent hover:underline not-italic"
        >
          Settings → Channels &amp; Paths (Engine Tuning subsection)
        </a>
        . Engine Editor absorbs at V2 (build-window).
      </p>
    </div>
  );
}

// ── Sub-region: Restock urgency tiers (§6.6.4) ──────────────────────────────
// restock_urgency_red/yellow multipliers are sub-shape 1b (code-PR / impl-home=data).
function RestockUrgencyTiersSubRegion(): JSX.Element {
  return (
    <div
      id="restock-urgency-tiers"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Restock urgency tiers</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Multipliers (`restock_urgency_red` = 1.0, `restock_urgency_yellow` = 1.5)
            flagging products below per-tier days-of-supply thresholds.
          </p>
        </div>
      </div>
      <p className="text-xs text-ink-muted italic pt-1">
        Default values: red 1.0, yellow 1.5. Default change requires code-PR +
        deployment.
      </p>
    </div>
  );
}

// ── Sub-region: Buy Score composition (§6.6.5) ──────────────────────────────
// Day 2 V1 4a positive-affordance content per scope-doc §2 amended bswc two-cell
// rendering framing — renders resolved-blend view with active metric labels
// (mirrors Atlas BuyScoreCompositionCard verbatim per scope-doc §3.2 copy
// principle) + cross-link affordance to dedicated edit route (single editing
// surface per Decision 1 relocated to dedicated route altitude).
//
// Substrate: direct reuse of Atlas substrate library per Sol direction at Day 2
// P0 (item 3 = (a)). Component fetches its own substrate via
// fetchTransformationRowSubstrate on mount, uses buyScoreComposition slice
// (activeBswcLabels) — same shape Atlas BuyScoreCompositionCard receives via
// parent DecisionRulesCluster prop-pass.
function BuyScoreCompositionSubRegion(): JSX.Element {
  const navigate = useNavigate();
  const [substrate, setSubstrate] = useState<BuyScoreCompositionSubstrate | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetchTransformationRowSubstrate()
      .then((s) => {
        if (!cancelled) setSubstrate(s.buyScoreComposition);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      id="buy-score-composition"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <Calculator size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">Buy Score composition</h3>

          {/* Resolved-blend view — mirrors Atlas BuyScoreCompositionCard verbatim */}
          {error ? (
            <p className="text-xs text-ink-muted italic mt-0.5">
              Buy Score composition substrate currently unavailable.
            </p>
          ) : !substrate ? (
            <div className="mt-1 h-4 bg-surface-4 rounded animate-pulse" aria-label="Loading Buy Score composition substrate" />
          ) : (
            <p className="text-xs text-ink-secondary mt-0.5">
              Buy Score — weighted blend of {substrate.activeBswcLabels.length} active{' '}
              {substrate.activeBswcLabels.length === 1 ? 'metric' : 'metrics'}:{' '}
              {joinList(substrate.activeBswcLabels)}.
            </p>
          )}
        </div>
      </div>

      {/* Cross-link affordance to dedicated edit route per scope-doc §2 amended */}
      <button
        type="button"
        onClick={() => navigate(URL_ENGINE_EDITOR_WEIGHT_COMPONENTS)}
        className="text-xs text-accent hover:underline cursor-pointer not-italic pt-1"
      >
        Edit at Weight components →
      </button>
    </div>
  );
}

// ── Cluster wrapper ─────────────────────────────────────────────────────────

export default function EngineEditorDecisionRulesCluster(): JSX.Element {
  return (
    <section
      id="decision-rules"
      data-cell-coordinate="transformation-decision"
      data-region-type="cluster"
      className="border border-border bg-surface-1 rounded-lg p-4 space-y-3"
    >
      {/* Cluster header per Atlas §6.6 verbatim inheritance */}
      <div>
        <h2 className="text-sm font-semibold text-ink-bright">Decision rules</h2>
        <p className="text-xs text-ink-secondary mt-1">
          Five named rules encoding ISG&apos;s operational gates and strategic ranking.
          Four rules gate which products pass; one rule ranks among products that pass.
        </p>
        <p className="text-xs text-ink-secondary mt-1">
          <span className="font-semibold text-ink-bright">Together:</span> ISG&apos;s
          encoded posture for what&apos;s worth buying.
        </p>
      </div>

      {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

      <div className="space-y-2">
        <EngineEditorMapEnforcementSubRegion />
        <CreditGateSubRegion />
        <PathEligibilitySubRegion />
        <RestockUrgencyTiersSubRegion />
        <BuyScoreCompositionSubRegion />
      </div>
    </section>
  );
}
